/**
 * MusicDirector — the game's music (OD-022; the owner's implementation note 16.9.2026). Every match has a song behind it:
 * the match picks one at random (from the songs tagged for that room, never the same song twice in
 * a row) and plays it from the top, so the intro runs once and playback then stays inside the track's own loop. When the
 * match ends the director schedules the exit: at the next allowed exit marker it stops the looping source and starts the
 * outro from the same cached buffer, both at ONE AudioContext timestamp, so the seam is sample-accurate however busy the
 * main thread is. Going back to a lobby or leaving the room fades the music out over a second (the owner's rule); that
 * fade is navigation, never a patch over a bad loop point.
 *
 * Timing is read from the audio clock only — no setTimeout, no `ended`, no <audio> events. AudioBuffers are decoded once
 * and cached; AudioBufferSourceNodes are one-shot and made per playback. Graph: source → its own gain (fades) → music
 * gain (half volume) → master gain (mute) → destination. The layer stays silent, without errors, while a file is missing.
 * Player toggle: Settings → Music; the global mute silences it too.
 */
import { signal } from '@preact/signals';
import type { Phase } from '@snw/protocol';
import { musicOn, muted } from '../state/store';
import { TRACKS, playsIn, type Track, type MusicRoom } from './tracks';
import { positionSample, samplesToNextExit } from './loopmath';

const FADE_MS = 1000; const VOLUME = 0.5;
const PLAYING: Phase[] = ['SPONSOR_BREAK', 'LINE_LOCK', 'INTRO', 'PREDICT', 'REVEAL', 'SCORE', 'ROUND_END'];
/** the song of the current (or the last) match — the 34A music card shows it */
export const nowPlaying = signal<Track | null>(null);

let ctx: AudioContext | null = null; let musicGain: GainNode | null = null; let master: GainNode | null = null;
const buffers = new Map<string, Promise<AudioBuffer | null>>();
/** the sources of the current song: the looping one, and the outro once it is scheduled */
let live: { track: Track; buffer: AudioBuffer; startedAt: number; loopSrc: AudioBufferSourceNode; gain: GainNode; outroSrc: AudioBufferSourceNode | null } | null = null;
let gen = 0;                                   // a decode that lands after a track change belongs to no one
let inMatch = false; let lastId = ''; let next: { room: MusicRoom; track: Track } | null = null;

function ensure(): boolean {
  if (ctx) return true;
  try {
    ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
    musicGain = ctx.createGain(); musicGain.gain.value = VOLUME; musicGain.connect(master);   // music sits at half volume
    return true;
  } catch { return false; }
}
/** mobile only starts an AudioContext from a gesture; the first tap anywhere is enough */
document.addEventListener('pointerdown', () => { if (ctx && ctx.state === 'suspended' && musicOn.value && !muted.value) void ctx.resume().catch(() => undefined); }, { passive: true });

function load(t: Track): Promise<AudioBuffer | null> {
  if (!ensure() || !ctx) return Promise.resolve(null);
  if (!buffers.has(t.file)) buffers.set(t.file, (async () => { // keyed by FILE: several entries may share one song, and it is decoded once. The SPA fallback answers a missing file with index.html, hence the type check
    try {
      const r = await fetch(`/audio/music/${t.file}`, { cache: 'force-cache' });
      const type = r.headers.get('content-type') ?? '';
      if (!r.ok || !ctx || type.startsWith('text/')) return null;
      return await ctx.decodeAudioData(await r.arrayBuffer());
    } catch { return null; }
  })());
  return buffers.get(t.file)!;
}
/** the decoded buffer is the truth: a marker past its end would never fire */
function specOf(t: Track, buf: AudioBuffer): { sampleRate: number; loopStartSample: number; loopEndSample: number; exitSamples: number[]; outroStartSample: number } {
  const total = Math.round(buf.duration * t.sampleRate);
  const clamp = (s: number) => Math.max(0, Math.min(total, s));
  const loopEndSample = clamp(t.loopEndSample); const loopStartSample = Math.min(clamp(t.loopStartSample), Math.max(0, loopEndSample - 1));
  return { sampleRate: t.sampleRate, loopStartSample, loopEndSample, exitSamples: t.exitSamples.map(clamp).filter((s) => s > loopStartSample), outroStartSample: clamp(t.outroStartSample || loopEndSample) };
}
function stopNow(): void {
  const l = live; live = null; gen += 1; if (!l || !ctx) return;
  const t = ctx.currentTime;
  l.gain.gain.cancelScheduledValues(t); l.gain.gain.setValueAtTime(l.gain.gain.value, t); l.gain.gain.linearRampToValueAtTime(0, t + FADE_MS / 1000);
  const end = t + FADE_MS / 1000 + 0.05;
  for (const s of [l.loopSrc, l.outroSrc]) { if (s) { try { s.stop(end); } catch { /* already stopped */ } } }
  // the owner's rule: 50 % → 0 in a second, and when it is at zero the player closes. The context sleeps rather than
  // being thrown away — a closed one cannot be reopened without a new gesture on mobile — and start() wakes it.
  const mine = gen; setTimeout(() => { if (gen === mine && !live && ctx && ctx.state === 'running') void ctx.suspend().catch(() => undefined); }, FADE_MS + 100);
}
function pick(room: MusicRoom): Track | null {
  /* A song plays ONLY where it is tagged, so nothing adult can wander into a room that did not ask for it. There is no
     borrowing: a room with no song of its own plays none. */
  const list = TRACKS.filter((t) => playsIn(t, room));
  if (!list.length) return null;
  const fresh = list.length > 1 ? list.filter((t) => t.id !== lastId) : list;
  return fresh[Math.floor(Math.random() * fresh.length)]!;
}
async function start(track: Track): Promise<void> {
  if (!ensure() || !ctx || !musicGain) return;
  const mine = ++gen;
  if (ctx.state !== 'running') { try { await ctx.resume(); } catch { return; } }
  const buf = await load(track);
  if (!buf || !ctx || !musicGain || mine !== gen || !inMatch || muted.value || !musicOn.value) return;
  const spec = specOf(track, buf);
  const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
  src.loopStart = spec.loopStartSample / spec.sampleRate; src.loopEnd = spec.loopEndSample / spec.sampleRate;
  const g = ctx.createGain(); g.gain.value = 1; src.connect(g); g.connect(musicGain);
  const at = ctx.currentTime; src.start(at);                       // from the top: the intro plays once, then the loop holds
  live = { track, buffer: buf, startedAt: at, loopSrc: src, gain: g, outroSrc: null };
}
/**
 * The match is over: leave the loop at the next allowed exit and let the outro play out. Both the stop and the outro's
 * start are scheduled at the same audio timestamp, so nothing is heard twice and nothing is cut.
 */
function requestOutro(): void {
  const l = live; if (!l || !ctx || !musicGain || l.outroSrc) return;
  const spec = specOf(l.track, l.buffer);
  const pos = positionSample(spec, ctx.currentTime - l.startedAt);
  const exitTime = ctx.currentTime + samplesToNextExit(spec, pos) / spec.sampleRate;
  const out = ctx.createBufferSource(); out.buffer = l.buffer; out.loop = false; out.connect(l.gain);
  try { l.loopSrc.stop(exitTime); } catch { /* already stopped */ }
  out.start(exitTime, spec.outroStartSample / spec.sampleRate);
  l.outroSrc = out;
}

/** Room drives this with the room's phase. A lobby prepares the next match's song; the first playing phase starts it. */
export function roomMusic(phase: Phase | null, room: MusicRoom): void {
  if (!phase) { inMatch = false; stopNow(); return; }                                    // left the room
  if (phase === 'LOBBY') {                                                               // a lobby is another view: fade, and get the next song ready
    inMatch = false; stopNow();
    if (!next || next.room.kind !== room.kind || next.room.k18 !== room.k18) { const t = pick(room); next = t ? { room, track: t } : null; }
    if (next && musicOn.value && !muted.value) void load(next.track);
    return;
  }
  if (phase === 'MATCH_END') { inMatch = false; requestOutro(); return; }                 // the song leaves the loop and plays out
  if (PLAYING.includes(phase) && !inMatch) {                                             // a match starts (the first one, or a rematch)
    inMatch = true; stopNow();
    const track = next && next.room.kind === room.kind && next.room.k18 === room.k18 ? next.track : pick(room); next = null;
    nowPlaying.value = track; if (!track) return;
    lastId = track.id;
    if (!muted.value && musicOn.value) void start(track);
  }
}
/** Settings → Music or the mute changed: stop now, or pick the current match's song up again */
export function musicApply(): void {
  if (muted.value || !musicOn.value) { stopNow(); return; }
  const t = nowPlaying.value; if (inMatch && t && !live) void start(t);
}
document.addEventListener('visibilitychange', () => { if (document.hidden) { if (ctx && ctx.state === 'running') void ctx.suspend().catch(() => undefined); } else if (ctx && live && musicOn.value && !muted.value) void ctx.resume().catch(() => undefined); });
// The music follows the mute itself (Marko 16.9.2026: the in-game speaker did not silence it). Every speaker in the game
// only flips `muted`; before this, only the Settings toggle happened to call musicApply() and the song played on.
muted.subscribe(() => musicApply()); musicOn.subscribe(() => musicApply());
